import React, { useEffect, useState } from 'react';
import { Box, Button, Input, Tooltip, InputGroup, InputRightAddon, useDisclosure, Text } from '@chakra-ui/react';
import './ManageParameters.scss';
import { manageParametersValidation } from '@/queue/ducks/validationSchema';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { callToGetDealerAction, setDealerActionTimeout } from '@/redux/app-slice';
import { getDealerActionTimeoutData } from '@/redux/app-selectors';
import { getIsUpdatingDealerctionTimeout } from '../ducks/selectors';
import { callToUpdateDealerActionTimeout } from '../ducks/slice';
import { keyType } from '@/queue/types';
import * as constants from '@/utils/constants';
import { DealerActionTimeout } from '@/model/profile.mjs';
import { minutesToHours, hoursToMinutes } from '@/utils/utils';
import { logAnalyticsEvent } from '@/utils/analyticsLogger';
import { analyticsEvents } from '@/utils/analyticsEvents';

interface Props{
  doRefresh: number;
}

function ManageParameters({doRefresh}: Props) {
  const [activeTooltip, setActiveTooltip] = useState(null); 

  enum FormFields {
    DEALER_UNRESPONSIVE_INITIATION =  "dealer_unresponsive_initiation",
    DEALER_UNRESPONSIVE_CONFIRMATION = "dealer_unresponsive_confirmation",
    DEALER_SMS_EMAIL_UPDATE_TIMEOUT =  "dealer_sms_email_update_timeout",
    FFL_SUPPORT_EMAIL_UPDATE_TIMEOUT =  "ffl_support_email_update_timeout",
    STORE_AGENT_EMAIL_UPDATE_TIMEOUT = "store_agent_email_update_timeout",
    CUSTOMER_ACTION_TIMEOUT =  "customer_action_timeout",
    DEALER_LAST_CHANCE_ACTION_TIMEOUT = "dealer_last_chance_action_timeout",
    DEALER_DOCUMENTS_PROCESSING_TIMEOUT = "dealer_documents_processing_timeout",
    DEALER_DOCUMENTS_REUPLOAD_TIMEOUT = "dealer_documents_reupload_timeout",
    CUSTOMER_REASSIGNMENT_EMAIL_REMINDER_TIMEOUT = "customer_reassignment_email_reminder_timeout",
  }

  const formMapping = [
    {
      label: 'Dealer SMS Email Update Timeout',
      inputType: 'number',
      fieldName: FormFields.DEALER_SMS_EMAIL_UPDATE_TIMEOUT,
      maxNumberLength: 6,
      tooltip: constants.DEALER_SMS_EMAIL_UPDATE_TIMEOUT_TOOL_TIP_CONTENT
    }
    , {
      label: 'FFL Support Email Update Timeout',
      inputType: 'number',
      fieldName: FormFields.FFL_SUPPORT_EMAIL_UPDATE_TIMEOUT,
      maxNumberLength: 6,
      tooltip: constants.FFL_SUPPORT_EMAIL_UPDATE_TIMEOUT_TOOL_TIP_CONTENT
    }, {
      label: 'Store Agent Email Update Timeout',
      inputType: 'number',
      fieldName: FormFields.STORE_AGENT_EMAIL_UPDATE_TIMEOUT,
      maxNumberLength: 6,
      tooltip: constants.STORE_AGENT_EMAIL_UPDATE_TIMEOUT_TOOL_TIP_CONTENT
    }, {
      label: 'Customer Action Timeout',
      inputType: 'number',
      fieldName: FormFields.CUSTOMER_ACTION_TIMEOUT,
      maxNumberLength: 6,
      tooltip: constants.CUSTOMER_ACTION_TIMEOUT_TOOL_TIP_CONTENT
    }, {
      label: 'Dealer Unresponsive Initiation Timeout',
      inputType: 'number',
      fieldName: FormFields.DEALER_UNRESPONSIVE_INITIATION,
      maxNumberLength: 6,
      tooltip: constants.DEALER_UNRESPONSIVE_INITIATION_TOOL_TIP_CONTENT
    }, {
      label: 'Dealer Last Chance Action Timeout',
      inputType: 'number',
      fieldName: FormFields.DEALER_LAST_CHANCE_ACTION_TIMEOUT,
      maxNumberLength: 6,
      tooltip: constants.DEALER_LAST_CHANCE_ACTION_TIMEOUT_TOOL_TIP_CONTENT
    }, {
      label: 'Dealer Unresponsive Confirmation Timeout',
      inputType: 'number',
      fieldName: FormFields.DEALER_UNRESPONSIVE_CONFIRMATION,
      maxNumberLength: 6,
      tooltip: constants.DEALER_UNRESPONSIVE_CONFIRMATION_TOOL_TIP_CONTENT
    }, {
      label: 'Dealer Documents Processing Timeout',
      inputType: 'number',
      fieldName: FormFields.DEALER_DOCUMENTS_PROCESSING_TIMEOUT,
      maxNumberLength: 6,
      tooltip: constants.DEALER_DOCUMENTS_PROCESSING_TIMEOUT_TOOL_TIP_CONTENT
    }, {
      label: 'Customer Reassignment Email Reminder Timeout',
      inputType: 'number',
      fieldName: FormFields.CUSTOMER_REASSIGNMENT_EMAIL_REMINDER_TIMEOUT,
      maxNumberLength: 6,
      tooltip: constants.CUSTOMER_REASSIGNMENT_EMAIL_REMINDER_TIMEOUT_TOOL_TIP_CONTENT
    }
  ];
  const dispatch = useDispatch();
  const dealerActionData = getDealerActionTimeoutData();
  const [isActionDisabled, setIsActionDisabled] = useState(true);
  const isUpdatingDealerctionTimeout = getIsUpdatingDealerctionTimeout();
 
  const initialData = {
      dealer_sms_email_update_timeout: dealerActionData.dealer_sms_email_update_timeout ? dealerActionData.dealer_sms_email_update_timeout : dealerActionData.dealer_sms_email_update_timeout == 0 ? 0 : 8*60,
      ffl_support_email_update_timeout : dealerActionData.ffl_support_email_update_timeout ? dealerActionData.ffl_support_email_update_timeout : dealerActionData.ffl_support_email_update_timeout == 0 ? 0 : 8*60,
      store_agent_email_update_timeout :  dealerActionData.store_agent_email_update_timeout ? dealerActionData.store_agent_email_update_timeout : dealerActionData.store_agent_email_update_timeout == 0 ? 0 : 8*60,
      customer_action_timeout: dealerActionData.customer_action_timeout ? dealerActionData.customer_action_timeout : 0,
      dealer_unresponsive_initiation : dealerActionData.dealer_unresponsive_initiation ? dealerActionData.dealer_unresponsive_initiation  : dealerActionData.dealer_unresponsive_initiation  == 0 ? 0 : 8*60,
      dealer_last_chance_action_timeout: dealerActionData.dealer_last_chance_action_timeout ? dealerActionData.dealer_last_chance_action_timeout : dealerActionData.dealer_last_chance_action_timeout == 0 ? 0 : 7*8*60,
      dealer_unresponsive_confirmation : dealerActionData.dealer_unresponsive_confirmation ? dealerActionData.dealer_unresponsive_confirmation : dealerActionData.dealer_unresponsive_confirmation == 0 ? 0 : 8*60,
      dealer_documents_processing_timeout : dealerActionData.dealer_documents_processing_timeout ? dealerActionData.dealer_documents_processing_timeout : dealerActionData.dealer_documents_processing_timeout == 0 ? 0 : 16*60, 
      customer_reassignment_email_reminder_timeout : dealerActionData.customer_reassignment_email_reminder_timeout ? dealerActionData.customer_reassignment_email_reminder_timeout : dealerActionData.customer_reassignment_email_reminder_timeout == 0 ? 0 : 16*60, // 2 days (working hours)
      transfer_sms_send_to_dealer_timeout:0
  };
  useEffect(() => {
         if(doRefresh === 4)
            dispatch(callToGetDealerAction())
      },[doRefresh])
  useEffect(() => {
    dispatch(callToGetDealerAction());
  }, []);

  const handleMouseOver = (id: any | string) => {
    setActiveTooltip(id); 
    onOpen()
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null); 
    onClose()
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    reset
  } = useForm({
    resolver: yupResolver(manageParametersValidation),
    mode: 'onChange',
    values: initialData
  });

  const submitHandler: SubmitHandler<
    DealerActionTimeout 
  > = (data) => {
    if (JSON.stringify(initialData) !== JSON.stringify(data)) {
      const payload = {
        ...(initialData.dealer_sms_email_update_timeout !== data.dealer_sms_email_update_timeout
          ? { dealer_sms_email_update_timeout: data.dealer_sms_email_update_timeout as number }
          : {}),
        ...(initialData.ffl_support_email_update_timeout !== data.ffl_support_email_update_timeout
          ? { ffl_support_email_update_timeout: data.ffl_support_email_update_timeout as number }
          : {}),
        ...(initialData.store_agent_email_update_timeout !== data.store_agent_email_update_timeout
          ? { store_agent_email_update_timeout: data.store_agent_email_update_timeout as number }
          : {}),
        ...(initialData.customer_action_timeout !== data.customer_action_timeout
          ? { customer_action_timeout: data.customer_action_timeout as number }
          : {}),
        ...(initialData.dealer_unresponsive_initiation !== data.dealer_unresponsive_initiation
          ? { dealer_unresponsive_initiation: data.dealer_unresponsive_initiation as number }
          : {}),
        ...(initialData.dealer_last_chance_action_timeout !== data.dealer_last_chance_action_timeout
          ? { dealer_last_chance_action_timeout: data.dealer_last_chance_action_timeout as number }
          : {}),
        ...(initialData.dealer_unresponsive_confirmation !== data.dealer_unresponsive_confirmation
          ? { dealer_unresponsive_confirmation: data.dealer_unresponsive_confirmation as number }
          : {}),
        ...(initialData.dealer_documents_processing_timeout !== data.dealer_documents_processing_timeout
          ? { dealer_documents_processing_timeout: data.dealer_documents_processing_timeout as number }
          : {}),
        ...(initialData.customer_reassignment_email_reminder_timeout !== data.customer_reassignment_email_reminder_timeout
          ? { customer_reassignment_email_reminder_timeout: data.customer_reassignment_email_reminder_timeout as number }
          : {}),  
        transfer_sms_send_to_dealer_timeout: 0                  
      };
      dispatch(callToUpdateDealerActionTimeout(payload));
      setDealerActionTimeout({...initialData,...payload})
      setIsActionDisabled(true);
    }
  };
  const onCancelHandler = () => {
    reset(initialData);
    setIsActionDisabled(true);
  };

  const onChangeFeild = () => {
    const formValues = getValues();
    setIsActionDisabled(
      !(JSON.stringify(formValues) !== JSON.stringify(initialData))
    );
  };
  const numericalRegexPattern = new RegExp('^[0-9]+$');
  const handleNonNumericalKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace') {
      return;
    }
    if (!numericalRegexPattern.test(event.key)) {
      event.preventDefault();
    }
  };

  const {isOpen, onOpen, onClose} = useDisclosure();

  const onclickToolTip = () => {
    if(isOpen)
        onClose();
    else 
        onOpen();
  }
  const handleSubmitFunction = handleSubmit(submitHandler);

  return (
    <Box>
      <form className="manage-parameters-profile-form">
        <ul className="manage-dealer-profile">
          <li style={{'listStyle': 'none'}}>
            <Box className="manage-parameters-header" mb={10}>
              Company parameters are configuration options that define how your company's data, operations, and user experience are managed within the software.
              The following parameters are all steps that can occur during the transfer workflow and are defined in minutes.
            </Box>
          </li>
          {formMapping.map((formItem: any) => {
            return (
              <>
                <li className="manage-parameters-profile-item">
                  <div className="manage-parameters-profile-label">
                    <div>{formItem.label}</div>
                  </div>
                  <div className="manage-parameters-profile-right-value">
                    <div className="mparameters-email-input-wrapper">
                      <div className="mparameters-email-input">
                        <InputGroup>
                          <Input
                            className="manage-parameters-profile-form__input"
                            {...register(formItem.fieldName , {
                              onChange: onChangeFeild,
                              setValueAs: (value: string) => {
                                const parsedValue = parseFloat(value);
                                return isNaN(parsedValue)
                                  ? undefined
                                  : parsedValue;
                              }
                            })}
                            maxLength={formItem.maxNumberLength}
                            onKeyDown={handleNonNumericalKeyDown}
                            _focus={{
                              borderRight: "none",
                              boxShadow: "none"
                            }}
                          />
                          <InputRightAddon 
                            bg="transparent" 
                            border="1px solid" 
                            borderColor="#000"
                            borderLeft="none"
                            borderTopRightRadius={"4px"}
                            borderBottomRightRadius={"4px"}
                          >minutes</InputRightAddon>
                        </InputGroup>
                        <Tooltip
                          hasArrow
                          placement="top"
                          label={formItem.tooltip}
                          maxWidth={'800px'}
                          color={'black'}
                          bg={'white'}
                          isOpen={activeTooltip === formItem.fieldName ? isOpen : false}
                        >
                          <div className={`input-tooltip-image ${activeTooltip === formItem.fieldName ? "input-tooltip-image-hovered" : ""}`} onClick={onclickToolTip} onMouseOver={() => { isOpen; handleMouseOver(formItem.fieldName) }} onMouseLeave={handleMouseLeave} />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </li>
                <li className="manage-parameters-profile-item">
                  <div>
                    {errors[formItem.fieldName as keyType<typeof errors>] &&
                      errors[formItem.fieldName as keyType<typeof errors>]
                        ?.message && (
                        <div className="form-error">
                          {
                            errors[formItem.fieldName as keyType<typeof errors>]
                              ?.message
                          }
                        </div>
                      )}
                  </div>
                </li>
              </>
            );
          })}
        </ul>
        <div className="manage-parameters-profile-btns">
          <Button
            colorScheme="blue"
            background="#ffffff"
            color="#0C1E46"
            border="1px solid #0C1E46"
            padding="8px 11px"
            marginRight="20px"
            onClick={onCancelHandler}
            isDisabled={isUpdatingDealerctionTimeout || isActionDisabled}
          >
            {constants.CANCEL_TEXT}
          </Button>
          <Button
            colorScheme="blue"
            background="#0C1E46"
            padding="8px 20px"
            onClick={()=>{logAnalyticsEvent(analyticsEvents.updateDealerActionTimeout); handleSubmitFunction();}}
            isLoading={isUpdatingDealerctionTimeout}
            isDisabled={isUpdatingDealerctionTimeout || isActionDisabled}
          >
            {constants.SAVE_TEXT}
          </Button>
        </div>
      </form>
    </Box>
  );
}

export default ManageParameters;
